import { Sparkles, ChevronRight } from "lucide-react";
import { GlassCard } from "@/components/system/GlassCard";
import ProvenanceTag from "@/components/system/ProvenanceTag";

export default function Placeholder({ title, subtitle, blurb, Icon }) {
  return (
    <div className="flex flex-col gap-8">
      <section className="nn-enter flex items-end justify-between gap-8 pt-2">
        <div>
          <div className="nn-eyebrow">NeuroNeeti · Module</div>
          <h1 className="mt-3 flex items-center gap-3 nn-h1 text-[hsl(var(--nn-ink))]">
            {Icon && (
              <span className="grid h-10 w-10 place-items-center rounded-xl border border-white/[0.08] bg-white/[0.04] text-[hsl(var(--nn-blue))]">
                <Icon className="h-4 w-4" strokeWidth={1.6} />
              </span>
            )}
            {title}
          </h1>
          <p className="mt-2 text-[14px] leading-[22px] text-[hsl(var(--nn-ink-muted))]">
            {subtitle}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="nn-chip nn-chip-blue">Design system ready</span>
          <span className="nn-chip">Draft · fellowship build</span>
        </div>
      </section>

      <GlassCard className="nn-enter nn-enter-1 nn-lift !p-6">
        <div className="grid grid-cols-[1fr_auto] items-start gap-8">
          <div>
            <div className="flex items-center gap-2 text-[hsl(var(--nn-amber))]">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
              <span className="nn-eyebrow" style={{ color: "hsl(var(--nn-amber))" }}>
                Coming next
              </span>
            </div>
            <p className="mt-4 max-w-2xl text-[15px] leading-[24px] text-[hsl(var(--nn-ink))]">
              {blurb}
            </p>
            <p className="mt-3 max-w-2xl text-[13px] leading-[22px] text-[hsl(var(--nn-ink-muted))]">
              This module inherits the NeuroNeeti shell: identical typography, spacing, glass
              surfaces, provenance tagging and animation rhythm. Detailed workflows are
              intentionally deferred so the design system stays consistent as each screen is
              built out.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <button className="nn-btn nn-btn-primary">
                Preview a sample layout
                <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.8} />
              </button>
              <button className="nn-btn">Open specification</button>
            </div>
          </div>

          <div className="nn-glass-inset w-[300px] p-5">
            <div className="nn-eyebrow">Shared components</div>
            <ul className="mt-3 flex flex-col gap-2 text-[12.5px] text-[hsl(var(--nn-ink-muted))]">
              {[
                "Glass card · header · toolbar",
                "Timeline · provenance ledger",
                "Conversation surface",
                "Table · dense case rows",
                "Modal · sheet · popover",
              ].map((s) => (
                <li key={s} className="flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-[hsl(var(--nn-blue))]" />
                  {s}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap gap-1.5">
              <ProvenanceTag label="Case" ref="NN-2038-C" />
              <ProvenanceTag kind="blue" label="Paper" ref="Yaşargil · 1996" />
            </div>
          </div>
        </div>
      </GlassCard>

      <div className="nn-enter nn-enter-2 grid grid-cols-3 gap-6">
        {[1, 2, 3].map((n) => (
          <GlassCard key={n} className="nn-lift !p-5">
            <div className="flex items-center justify-between">
              <div className="nn-eyebrow">Placeholder · {String(n).padStart(2, "0")}</div>
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--nn-blue))]/70" />
            </div>
            <div className="mt-5 h-2.5 w-3/4 rounded-full bg-white/[0.07]" />
            <div className="mt-2 h-2.5 w-1/2 rounded-full bg-white/[0.05]" />
            <div className="mt-6 flex items-center justify-between">
              <ProvenanceTag label="Case" ref={`NN-20${30 + n}-x`} />
              <span className="text-[10.5px] font-mono text-[hsl(var(--nn-ink-faint))]">
                pending
              </span>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}
